import React from "react";
import dayjs from "../../../../utils/dayjs";
import { motion } from "framer-motion";
import { STATUS_COLORS } from "../data/mockData";
import { getSubjectTheme, formatScheduleEventHint } from "../subjectThemeUtils";
import { Clock, MapPin, User, FileText } from "lucide-react";

const MobileAgenda = ({
  events,
  currentDate,
  setCurrentDate,
  onEventClick,
}) => {
  // ✅ Luôn bắt đầu từ Thứ 2 của tuần hiện tại (không mutate state).
  // isoWeek thay cho .day(1): khi currentDate là Chủ nhật, .day(1) nhảy sang Thứ 2 tuần sau.
  const startOfWeek = dayjs(currentDate).startOf("isoWeek");
  const days = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, "day"));

  // Lọc sự kiện cho ngày được chọn (vẫn giữ logic CN = 7)
  const selectedDayIndex = currentDate.day() === 0 ? 7 : currentDate.day();
  const dayEvents = events
    .filter((ev) => ev.day === selectedDayIndex)
    .sort((a, b) => a.start - b.start);

  return (
    <div className="flex flex-col gap-6">
      {/* 1. Thanh chọn ngày nhanh - Fixed Start từ Thứ 2 */}
      <div className="flex justify-between items-center bg-white p-2 rounded-3xl border border-gray-100 shadow-sm">
        {days.map((date, i) => {
          const isSelected = date.isSame(currentDate, "day");
          const isToday = date.isSame(dayjs(), "day");

          return (
            <button
              key={i}
              onClick={() => setCurrentDate(date)}
              className={`flex flex-col items-center justify-center w-12 py-3 rounded-2xl transition-all ${
                isSelected
                  ? "bg-red-700 text-white shadow-lg shadow-red-200 scale-110 z-10"
                  : "text-gray-400 hover:bg-gray-50"
              }`}
            >
              <span
                className={`text-[10px] font-bold uppercase ${
                  isSelected ? "text-red-100" : "text-gray-400"
                }`}
              >
                {date.format("ddd")}
              </span>
              <span className="text-sm font-black">{date.format("DD")}</span>
              {isToday && !isSelected && (
                <div className="w-1 h-1 bg-red-50 rounded-full mt-1"></div>
              )}
            </button>
          );
        })}
      </div>

      {/* 2. Tiêu đề ngày hiển thị */}
      <div className="px-2">
        <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight">
          {currentDate.isSame(dayjs(), "day")
            ? "Hôm nay"
            : currentDate.format("dddd")}
        </h2>
        <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">
          {dayEvents.length} Lớp học được tìm thấy
        </p>
      </div>

      {/* 3. Danh sách lớp học (Timeline Style) */}
      <div className="space-y-4 px-1">
        {dayEvents.length > 0 ? (
          dayEvents.map((ev, idx) => {
            const theme = getSubjectTheme(ev);
            const status = STATUS_COLORS[ev.status];

            return (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                key={ev.id}
                onClick={() => onEventClick(ev, currentDate)}
                title={formatScheduleEventHint(ev)}
                className="relative bg-white rounded-[2rem] p-5 shadow-sm border border-gray-100 active:scale-[0.97] transition-all overflow-hidden"
              >
                {/* Thanh màu bên cạnh */}
                <div
                  className="absolute left-0 top-0 bottom-0 w-2"
                  style={{ backgroundColor: theme.accent }}
                />

                <div className="flex justify-between items-start mb-4 gap-2 flex-wrap">
                  <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full">
                    <Clock size={14} className="text-gray-400" />
                    <span className="text-xs font-black text-gray-700">
                      {ev.start}:00 - {ev.end}:00
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span
                      className={`text-[9px] px-2.5 py-1 rounded-lg font-black border uppercase tracking-wider ${
                        ev.venue === "school"
                          ? "bg-amber-50 text-amber-800 border-amber-200"
                          : "bg-slate-50 text-slate-600 border-slate-200"
                      }`}
                    >
                      {ev.venue === "school" ? "Tại trường" : "Trung tâm"}
                    </span>
                    <span
                      className={`text-[9px] px-2.5 py-1 rounded-lg font-black border uppercase tracking-wider ${status.color}`}
                    >
                      {status.label}
                    </span>
                  </div>
                </div>

                <h3 className="text-lg font-extrabold text-gray-800 leading-tight mb-2">
                  {ev.class}
                </h3>

                {(ev.subject || ev.course) && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {ev.subject ? (
                      <span
                        className={`inline-flex max-w-full px-2.5 py-1 rounded-lg border text-[9px] font-black uppercase tracking-wide ${theme.border} ${theme.text} bg-white/40 truncate`}
                        title={ev.subject}
                      >
                        {ev.subject}
                      </span>
                    ) : null}
                    {ev.course ? (
                      <span
                        className="inline-flex max-w-full px-2.5 py-1 rounded-lg border border-gray-200 text-[9px] font-bold bg-white/40 text-gray-600 truncate"
                        title={ev.course}
                      >
                        {ev.course}
                      </span>
                    ) : null}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-y-3">
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center">
                      <User size={14} />
                    </div>
                    <span className="text-xs font-bold">{ev.teacher}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 justify-end">
                    <span className="text-xs font-bold">{ev.room}</span>
                    <div className="w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center">
                      <MapPin size={14} />
                    </div>
                  </div>
                </div>

                {ev.note && (
                  <div className="mt-4 pt-4 border-t border-dashed border-gray-100 flex items-start gap-3">
                    <FileText size={16} className="text-gray-300 shrink-0" />
                    <p className="text-[11px] text-gray-400 italic font-medium leading-relaxed">
                      {ev.note}
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })
        ) : (
          <div className="py-20 text-center bg-white rounded-[3rem] border border-dashed border-gray-200">
            <p className="text-gray-400 font-bold italic uppercase tracking-widest text-xs">
              Trống lịch dạy
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileAgenda;
